import { useThree } from '@react-three/fiber'
import { Bloom, EffectComposer, N8AO, Outline, SMAA, ToneMapping } from '@react-three/postprocessing'
import { ToneMappingMode } from 'postprocessing'
import { useLayoutEffect, useRef, useState } from 'react'
import { Color } from 'three'
import { inverterAces } from './curvaAces'
import {
  decidirPosProcessamento,
  efeitosExtrasDaBusca,
  RECURSOS_QUALIDADE,
  SEM_EFEITOS,
  type NivelQualidade,
} from './qualidadeGrafica'
import { usePedidosPosProcessamento } from './registroPosProcessamento'

/**
 * Com o composer montado, a curva do tone mapping passa a valer sobre o quadro
 * inteiro — fundo incluso —, e o azul-marinho do estúdio virava quase preto. Enquanto
 * o composer existe, o fundo é trocado pela cor que a curva devolve como a
 * original, e volta ao que era quando o composer sai. Assim o fundo não muda ao
 * alternar entre os níveis de qualidade.
 */
function FundoCompensado() {
  const cena = useThree((estado) => estado.scene)
  const renderizador = useThree((estado) => estado.gl)

  useLayoutEffect(() => {
    const original = cena.background
    if (!(original instanceof Color)) return

    const compensada = inverterAces([original.r, original.g, original.b], renderizador.toneMappingExposure)
    if (!compensada) return

    cena.background = new Color(compensada[0], compensada[1], compensada[2])
    return () => {
      cena.background = original
    }
  }, [cena, renderizador])

  return null
}

/**
 * O pedaço do passe do N8AO que se usa aqui. O pacote `n8ao` é dependência do
 * wrapper do R3F, não nossa, e não traz tipos.
 */
interface PasseOclusao {
  autoDetectTransparency: boolean
  configuration: { transparencyAware: boolean }
}

/**
 * Oclusão de ambiente: escurece os cantos e os encontros entre peças — pneu
 * contra asfalto, vítima contra o banco, objeto contra o chão — que a
 * iluminação por mapa de ambiente sozinha deixa chapados.
 *
 * O raio é em metros do mundo (o veículo tem ~4 m de comprimento): raio grande
 * demais escurece o teto inteiro por dentro.
 */
function OclusaoAmbiente({ amostras, amostrasDoFiltro }: { amostras: number; amostrasDoFiltro: number }) {
  const passe = useRef<PasseOclusao>(null)

  useLayoutEffect(() => {
    const oclusao = passe.current
    if (!oclusao) return
    // O N8AO varre a cena inteira a cada quadro à procura de material
    // transparente e, ao achar um, passa a desenhar a cena MAIS DUAS VEZES por
    // quadro para tratar a transparência. Esta cena tem vidros e camadas
    // translúcidas em quase todos os modos, então isso triplicaria as chamadas
    // de desenho. Desligar a detecção evita a varredura e os dois desenhos; o
    // custo é a oclusão também escurecer um pouco por trás de vidro.
    oclusao.autoDetectTransparency = false
    oclusao.configuration.transparencyAware = false
  }, [])

  return (
    <N8AO
      ref={passe}
      halfRes
      aoRadius={0.5}
      distanceFalloff={1}
      intensity={2.2}
      aoSamples={amostras}
      denoiseSamples={amostrasDoFiltro}
      denoiseRadius={8}
    />
  )
}

interface Props {
  qualidade: NivelQualidade
  /** A cena é predominantemente opaca (Table top): a oclusão de ambiente ajuda. */
  oclusaoAmbiente?: boolean
}

/**
 * Único `EffectComposer` da cena: oclusão de ambiente, brilho, contornos e
 * suavização de bordas.
 *
 * Só existe um porque dois composers no mesmo `<Canvas>` disputam o quadro, e o
 * wrapper do R3F zera o tone mapping do renderer enquanto há um montado — era
 * isso que fazia a cena inteira mudar de aparência ao selecionar uma peça. Aqui
 * o `ToneMapping` entra explicitamente, depois do brilho (que precisa dos
 * valores acima de 1 antes de serem comprimidos) e antes do contorno (que deve
 * sair na cor escolhida, sem passar pela curva).
 *
 * Por padrão só os contornos montam o composer. Oclusão de ambiente e brilho
 * ficam atrás de `?efeitos=extras`: ver `efeitosExtrasDaBusca`. Nada é montado
 * sem pedido: ver `decidirPosProcessamento`.
 */
export function PosProcessamento({ qualidade, oclusaoAmbiente = false }: Props) {
  const { contornos, emissores } = usePedidosPosProcessamento()
  const [extras] = useState(() => typeof window !== 'undefined' && efeitosExtrasDaBusca(window.location.search))
  const temContorno = contornos.selecao.length > 0 || contornos.deformadas.length > 0

  const decisao = decidirPosProcessamento(extras ? RECURSOS_QUALIDADE[qualidade].efeitos : SEM_EFEITOS, {
    oclusaoAmbiente,
    emissores,
    contorno: temContorno,
  })

  if (!decisao.composer) return null

  return (
    <>
      <FundoCompensado />
      {/* `autoClear={false}` é exigido pelo `Outline`: com o padrão o contorno não
          é desenhado (o wrapper avisa no console). `multisampling={0}`: o
          antialiasing do canvas não alcança o buffer do composer, então quem
          suaviza as bordas é o SMAA no fim da cadeia. */}
      <EffectComposer autoClear={false} multisampling={0} enableNormalPass={false}>
        {decisao.oclusaoAmbiente && (
          <OclusaoAmbiente
            amostras={decisao.oclusaoAmbiente.amostras}
            amostrasDoFiltro={decisao.oclusaoAmbiente.amostrasDoFiltro}
          />
        )}
        {decisao.bloom && (
          // O limiar fica acima de 2 de propósito: fogo e faíscas têm emissão de
          // 2,8 a 7, e todo o resto da cena fica abaixo de ~1,2. Um limiar de 1
          // faria o reflexo do verniz na banca de luz do estúdio brilhar também.
          <Bloom luminanceThreshold={2} luminanceSmoothing={0.2} intensity={0.8} mipmapBlur />
        )}
        <ToneMapping mode={ToneMappingMode.ACES_FILMIC} />
        {contornos.selecao.length > 0 && (
          <Outline
            selection={[...contornos.selecao]}
            visibleEdgeColor="#38bdf8"
            hiddenEdgeColor="#000000"
            edgeStrength={4}
            width={1024}
            blur={false}
            xRay={false}
          />
        )}
        {contornos.deformadas.length > 0 && (
          <Outline
            selection={[...contornos.deformadas]}
            visibleEdgeColor="#f97316"
            hiddenEdgeColor="#7c2d12"
            edgeStrength={2.5}
            width={1024}
            blur={false}
            xRay={false}
          />
        )}
        <SMAA />
      </EffectComposer>
    </>
  )
}
